import { MobileIcon, Pencil2Icon } from '@radix-ui/react-icons';
import { Flex, IconButton } from '@radix-ui/themes';
import { CustomIcon } from 'components/common/custom-icon';
import { ErrorBoundary } from 'components/common/error-boundary';
import { SearchHitter } from 'components/common/hitters/search-hitter';
import { CommonTooltip } from 'components/common/tooltip';
import { PitchListSidebarSectionTitle } from 'components/sections/pitch-list/sidebar/section-title';
import { HittersContext } from 'contexts/hitters.context';
import { CustomIconPath } from 'enums/custom.enums';
import { CrudAction } from 'enums/tables';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext } from 'react';

const PlayerAppIcon = (props: { show: boolean; linked: boolean }) => {
  const { show, linked } = props;

  return (
    <CommonTooltip
      trigger={
        <IconButton
          size="1"
          className="btn-floating"
          variant={RADIX.BUTTON.VARIANT.BORDERLESS}
          color={RADIX.COLOR.NEUTRAL}
          style={{
            visibility: show ? 'visible' : 'hidden',
            pointerEvents: show ? 'auto' : 'none',
            background: 'transparent',
            cursor: 'default',
          }}
        >
          {/* dumb alignment fix for slightly different svg paths in custom and radix icons */}
          <Flex
            align="center"
            justify="center"
            style={{ margin: linked ? '0' : '1px -4px 0 0' }}
          >
            {linked ? (
              <MobileIcon />
            ) : (
              <CustomIcon icon={CustomIconPath.MobileCrossed} />
            )}
          </Flex>
        </IconButton>
      }
      text={
        linked
          ? 'hitters.player-app-access-enabled'
          : 'hitters.player-app-access-missing'
      }
    />
  );
};

export const PLSidebarHitterControls = () => {
  const { active: activeHitter, openCrudDialog } = useContext(HittersContext);
  const playerAppEnabled = useFeatureFlag('player-app');

  const onEdit = () => {
    if (!activeHitter) {
      return;
    }

    openCrudDialog({
      key: Date.now(),
      action: CrudAction.Update,
      models: [activeHitter],
    });
  };

  const showEditButton = playerAppEnabled && !!activeHitter;

  // player has Player App access
  const hitterLinked = !!(activeHitter?._mx_phone && activeHitter?.player_id);

  return (
    <ErrorBoundary identifier="PLSidebarHitterControls">
      <Flex direction="column" gap="2">
        <Flex align="center">
          <PitchListSidebarSectionTitle label="hitters.hitter" />
          <Flex ml="auto" align="center">
            {/* we render the player app status icon within a button for styling consistency with the edit hitter button */}
            <PlayerAppIcon show={showEditButton} linked={hitterLinked} />
            <IconButton
              size="1"
              onClick={onEdit}
              title={t('hitters.edit-hitter').toString()}
              className="btn-floating"
              variant={RADIX.BUTTON.VARIANT.BORDERLESS}
              color={RADIX.COLOR.NEUTRAL}
              style={{
                visibility: showEditButton ? 'visible' : 'hidden',
                pointerEvents: showEditButton ? 'auto' : 'none',
              }}
            >
              {/* This radix icon is not centered within its container for some reason */}
              <Pencil2Icon style={{ margin: '0 1px 1px 0' }} />
            </IconButton>
          </Flex>
        </Flex>
        <SearchHitter />
      </Flex>
    </ErrorBoundary>
  );
};
